import { forwardRef, KeyboardEvent, ReactNode } from "react";
import { Check } from "lucide-react";
import { FOCUS_RING, cx } from "./tokens";

/**
 * Semantic weight of a chip. `select` is the one selection treatment used by
 * every filter option in the product. `danger` exists only for options whose
 * own meaning is failure — "Incorretas" — never as emphasis (§23.6).
 */
export type ChipTone = "select" | "danger" | "warn";

export interface FilterChipProps {
  label: string;
  selected?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  tone?: ChipTone;
  /** Radio semantics instead of toggle semantics, for single-choice groups. */
  role?: "radio";
  onToggle?: () => void;
  tabIndex?: number;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  className?: string;
}

const TONE_SELECTED: Record<ChipTone, string> = {
  select: "border-select/[0.42] bg-select/[0.14] text-select-ink",
  danger: "border-danger/[0.42] bg-danger/[0.14] text-danger-ink",
  warn: "border-warn/[0.42] bg-warn/[0.14] text-warn-ink",
};

/**
 * The chip treatment, exported so single-choice groups can render the exact
 * same control without duplicating the styling (§24.2).
 *
 * Unselected chips stay neutral in both themes — a grid where every chip
 * carries a tinted border stops communicating what "selected" means (§8.1).
 */
export function chipClassName({
  selected = false,
  tone = "select",
}: {
  selected?: boolean;
  tone?: ChipTone;
}): string {
  return cx(
    "inline-flex h-9 max-w-full items-center gap-2 rounded-chip border px-3.5 sm:max-w-[18rem]",
    "text-[13px] font-medium leading-none transition-colors duration-150",
    "disabled:cursor-not-allowed disabled:opacity-50",
    FOCUS_RING,
    selected
      ? TONE_SELECTED[tone]
      : "border-edge bg-canvas text-ink-secondary hover:border-edge-strong hover:bg-surface-hover hover:text-ink",
  );
}

/**
 * A single filter option.
 *
 * Selection is carried by the check mark and aria-pressed as well as by colour,
 * so it survives a screen reader and a colour-blind reader alike (§23.16).
 * Labels truncate at the end, never at the start, and always keep the full text
 * available through `title` and `aria-label` (§23.13).
 */
const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(
  {
    label,
    selected = false,
    disabled = false,
    icon,
    tone = "select",
    role,
    onToggle,
    tabIndex,
    onKeyDown,
    className,
  },
  ref,
) {
  const selectionProps =
    role === "radio"
      ? ({ role: "radio", "aria-checked": selected } as const)
      : ({ "aria-pressed": selected } as const);

  return (
    <button
      ref={ref}
      type="button"
      data-chip
      disabled={disabled}
      title={label}
      aria-label={label}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      onClick={onToggle}
      className={cx(chipClassName({ selected, tone }), className)}
      {...selectionProps}
    >
      {icon ? (
        <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="truncate">{label}</span>
      {selected ? (
        <Check className="ml-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={3} aria-hidden="true" />
      ) : null}
    </button>
  );
});

export default FilterChip;
